// THE SERIES VOCABULARY, VERSION 1 (BUILD-BRIEF-R6 §2.2) — statistics about nobody in
// particular: a canton, a district, a commune, a réseau's own history. Never a raster cell (the
// raster is per organisation, §1.1's row 3) and never a link (nothing is named). `SERIES` names
// every series a module may emit; a module that emits an unknown `series_key` or a dimension
// token outside `DIMENSIONS` fails its dry run — this file is the contract, the way the raster
// is the contract for a cell.
//
// Labels come out of `seriesLabel(lang, key)` / `dimLabel(lang, dim, token)` — like raster
// labels, these are NOT i18n catalogue keys.
//
// The row shape (`SeriesPoint`) lives here too, beside its own vocabulary — the same deliberate
// deviation from §7 as `sources.v1.ts` (see that file's banner): two wave-1 packages would
// otherwise have had to share `src/shared/types.ts`.

export type SeriesGeoLevel = 'ch' | 'canton' | 'district' | 'commune' | 'organisation';
export const SERIES_GEO_LEVELS: readonly SeriesGeoLevel[] = ['ch', 'canton', 'district', 'commune', 'organisation'];

export type SeriesPeriodKind = 'year' | 'month' | 'date';
export const SERIES_PERIOD_KINDS: readonly SeriesPeriodKind[] = ['year', 'month', 'date'];

export type SeriesUnit =
  | 'persons' | 'households' | 'dossiers' | 'pct' | 'per_mille' | 'chf' | 'places' | 'fte'
  | 'cases' | 'projects' | 'institutions' | 'days' | 'measures' | 'organisations';
export const SERIES_UNITS: readonly SeriesUnit[] = [
  'persons', 'households', 'dossiers', 'pct', 'per_mille', 'chf', 'places', 'fte', 'cases',
  'projects', 'institutions', 'days', 'measures', 'organisations',
];

export interface SeriesSpec {
  key: string;                          // 'vd.ri.rate_18_64_pct'
  en: string; de: string; fr: string;   // the label; like raster labels, not i18n keys
  unit: SeriesUnit;
  geo_level: SeriesGeoLevel;
  period_kind: SeriesPeriodKind;
  dims: string[];                       // the dimension keys a point may carry, from DIMENSIONS
  source_key: string;                   // the PRIMARY source — what the Series zone shows by default
  also_from?: string[];                 // further sources that print the same quantity; shown beside the primary, never merged with it
  definition: string;                   // what the source counts, in the source's own words — never scored
}

// ── the dimension tokens ──────────────────────────────────────────────────────────────────────
//
// Where §2.2 elides a list with '…' the tokens below are read from the reconnaissance fixtures
// themselves (never invented): `offence_type` from T19.04.01's shared strings (statvd-lavi),
// `service_type` from Usagers_ESE.xlsx's combination-of-prestations columns (ese-etablissements),
// `request_type`/`motif` from the DGEJ stats brochure §1.1/§1.3 tables, `placement_type` from its
// §4.3 table, `domain` from the BCI catalogue's 230 records (nine distinct `domain` values,
// verified — dgej-protection-enfance and bci-projets-finances fixtures respectively). Each
// comment below names the exact paragraph the tokens came from so a later wave can extend, not
// re-derive, this list. `household_type` names no household composition breakdown found in any
// wave-1/2/3 fixture read so far; the five tokens are the ordinary Swiss social-statistics split
// and are a placeholder for the module that first needs it — a wave-2/3 parser that finds a
// different breakdown extends this array rather than filing an unlisted token.

const REGIME_EXTRA_TOKENS = [
  'pc_familles', 'pc_avs_ai', 'lamal_subsidy', 'rente_pont', 'laprams', 'housing_aid',
  'maternity', 'alimony_advance',
] as const;

export const DIMENSIONS: Record<string, readonly string[]> = {
  // network.v1.ts's REGIME_TOKENS plus the series-only regimes the cards' tables print (§2.2).
  regime: [
    'ri', 'ai', 'laci', 'asylum', 'refugee_integration', 'adult_basic_education',
    'post_compulsory', 'child_youth_protection', 'cantonal_disability', 'health', 'justice',
    'schools', 'private', 'other', ...REGIME_EXTRA_TOKENS,
  ],
  age_band: ['0_6', '7_15', '16_plus', '18_64', 'lt6', '6_15', '65_plus', 'all'],
  gender: ['f', 'm', 'nb', 'all'],
  // T19.04.01's seven categories, folded (statvd-lavi, shared strings verified 2026-09-05).
  offence_type: [
    'homicides', 'lesions_corporelles', 'actes_ordre_sexuel_enfants',
    'autres_atteintes_integrite_sexuelle', 'accidents_circulation_routiere',
    'autres_infractions', 'sans_indication',
  ],
  claimant_status: ['victim', 'relative'],
  // Usagers_ESE.xlsx's combination-of-prestations columns (§3.4 #5): AT/CJ/HEB and their unions.
  service_type: [
    'hebergement', 'centre_de_jour', 'atelier', 'ensemble',
    'centre_de_jour_atelier', 'hebergement_atelier', 'hebergement_centre_de_jour',
    'hebergement_centre_de_jour_atelier',
  ],
  problem: [
    'deficience_intellectuelle', 'troubles_psychiques', 'addiction', 'deficience_physique',
    'deficience_sensorielle', 'polyhandicap', 'autre', 'sans_indication', 'tous',
  ],
  mode: ['prescolaire', 'parascolaire', 'familial', 'collectif', 'total'],
  measure: [
    'authorised', 'offered', 'offered_fte', 'offered_morning', 'offered_noon',
    'offered_afternoon', 'authorised_incl_own', 'authorised_extra_pupils',
    'offered_fte_prescolaire', 'offered_fte_parascolaire', 'subsidised',
  ],
  staff_function: [
    'direction_pedagogique', 'educatif_tertiaire', 'educatif_secondaire_ii', 'educatif_ape',
    'stagiaire_tertiaire', 'apprenti_ase', 'direction_administrative', 'administration',
    'logistique', 'total',
  ],
  institution_type: [
    'collectif_subventionne', 'collectif_subventionne_elargi', 'collectif_subventionne_restreint',
    'collectif_non_subventionne', 'ecole_privee', 'total',
  ],
  contributor: ['etat', 'employeurs', 'communes', 'loterie_romande'],
  // DGEJ stats brochure §4.3's table (institutions PSE/non-PSE/hors canton, familles d'accueil,
  // studios, pensions, lieux d'accueil mère-enfant).
  placement_type: [
    'institution_pse', 'institution_non_pse', 'institution_hors_canton',
    'famille_accueil_vaudoise', 'famille_accueil_elargie', 'famille_accueil_hors_canton',
    'studio', 'pension', 'lieu_accueil_mere_enfant',
  ],
  // DGEJ stats brochure §1.1's table ("par provenance / type de requête").
  request_type: [
    'signalement', 'demande_aide', 'mandat_judiciaire', 'rapport_police',
    'information_sans_signalement',
  ],
  // DGEJ stats brochure §1.3's table ("par motif").
  motif: [
    'abus_ordre_sexuel', 'circonstances_entravant_capacite_parentale', 'difficultes_educatives',
    'mauvais_traitements_physiques', 'mauvais_traitements_psychiques',
    'mise_en_danger_par_lui_meme', 'negligences_carences', 'problemes_droit_de_visite',
    'violence_domestique',
  ],
  // The BCI catalogue's own nine `domain` values (projects_all.json, 230 records, verified
  // 2026-09-05: LANGUE_FORMATIONS · PROTECTION_DISCRIMINATION · CONSEIL · EMPLOYABILITE ·
  // FORFAITS · VIVRE_ENSEMBLE · PRIMO_INFORMATION · INFORMATION_ET_CONSEIL · PETITE_ENFANCE).
  domain: [
    'langue_formations', 'protection_discrimination', 'conseil', 'employabilite', 'forfaits',
    'vivre_ensemble', 'primo_information', 'information_et_conseil', 'petite_enfance',
  ],
  // Placeholder (see the file banner): no wave-1/2/3 fixture read so far breaks a series down by
  // household composition; the ordinary Swiss social-statistics split, extended by whichever
  // module first needs a different one.
  household_type: ['single', 'single_parent', 'couple', 'couple_with_children', 'other'],
  // §0.4's deliberate addition (deviation #2, noted by the planner): Lausanne's own direction
  // names (budget2025.txt, the Subventions section, folded to snake_case, in the order the six
  // "SUBVENTIONS TOTALES DE LA DIRECTION" blocks print them, verified 2026-09-05).
  department: [
    'culture_et_developpement_urbain', 'securite_et_economie', 'sports_et_cohesion_sociale',
    'logement_environnement_et_architecture', 'enfance_jeunesse_et_quartiers',
    'finances_et_mobilite',
  ],
  budget_kind: ['budget', 'comptes'],
};

// ── the wave-1 series (§3.5's fourteen; waves 2–3 append, never replace) ────────────────────

export const SERIES: SeriesSpec[] = [
  {
    key: 'vd.faje.funding_share_pct',
    en: 'FAJE funding share', de: 'FAJE-Finanzierungsanteil', fr: 'Part de financement de la FAJE',
    unit: 'pct', geo_level: 'canton', period_kind: 'year', dims: ['contributor'],
    source_key: 'faje-vd.ch', also_from: ['cour-des-comptes-vd'],
    definition: "la part des ressources financières de la FAJE apportée par chaque contributeur (État de Vaud, employeurs, communes, Loterie Romande), en % du total, telle que publiée dans le rapport annuel «La FAJE en chiffres».",
  },
  {
    key: 'vd.faje.resources_chf',
    en: 'FAJE total resources', de: 'FAJE-Gesamtressourcen', fr: 'Ressources financières totales de la FAJE',
    unit: 'chf', geo_level: 'canton', period_kind: 'year', dims: [],
    source_key: 'faje-vd.ch',
    definition: "le total des ressources financières de la FAJE pour l'année, tel que publié dans le rapport annuel.",
  },
  {
    key: 'vd.faje.contribution_chf',
    en: 'FAJE contribution by contributor', de: 'FAJE-Beitrag nach Beitragszahler', fr: 'Contribution à la FAJE par contributeur',
    unit: 'chf', geo_level: 'canton', period_kind: 'year', dims: ['contributor'],
    source_key: 'faje-vd.ch',
    definition: "le montant, en francs, apporté par chaque contributeur à la FAJE pour l'année, tel que publié dans le rapport annuel.",
  },
  {
    key: 'vd.faje.networks_recognised',
    en: 'FAJE-recognised réseaux', de: 'von der FAJE anerkannte Netzwerke', fr: 'Réseaux reconnus par la FAJE',
    unit: 'organisations', geo_level: 'canton', period_kind: 'year', dims: [],
    source_key: 'faje-vd.ch',
    definition: "le nombre de réseaux d'accueil de jour des enfants reconnus par la FAJE, tel que publié dans la liste des réseaux.",
  },
  {
    key: 'vd.faje.structures_subsidised',
    en: 'Structures subsidised by FAJE', de: 'von der FAJE subventionierte Strukturen', fr: 'Structures subventionnées par la FAJE',
    unit: 'institutions', geo_level: 'canton', period_kind: 'year', dims: [],
    source_key: 'faje-vd.ch',
    definition: "le nombre de structures d'accueil de jour subventionnées par la FAJE, tel que publié dans le rapport annuel.",
  },
  {
    key: 'vd.faje.operators',
    en: 'FAJE-funded operators', de: 'von der FAJE finanzierte Betreiber', fr: 'Opérateurs financés par la FAJE',
    unit: 'organisations', geo_level: 'canton', period_kind: 'year', dims: [],
    source_key: 'faje-vd.ch',
    definition: "le nombre d'opérateurs (associations, fondations, communes) recevant un financement de la FAJE, tel que publié dans le rapport annuel.",
  },
  {
    key: 'vd.aje.fte',
    en: 'AJE staff, FTE', de: 'AJE-Personal, VZÄ', fr: 'Personnel AJE, EPT',
    unit: 'fte', geo_level: 'organisation', period_kind: 'year', dims: ['staff_function'],
    source_key: 'statvd-accueil-jour-enfants',
    definition: "le nombre d'équivalents plein temps (EPT) par fonction, en accueil collectif subventionné par la FAJE et par réseau, tel que publié par StatVD (T_EPT_fonctions_accueil_subventionne_vaud.xlsx).",
  },
  {
    key: 'vd.aje.places',
    en: 'AJE places', de: 'AJE-Plätze', fr: "Places d'accueil de jour des enfants",
    unit: 'places', geo_level: 'organisation', period_kind: 'year', dims: ['mode', 'measure', 'institution_type'],
    source_key: 'statvd-accueil-jour-enfants', also_from: ['cour-des-comptes-vd'],
    definition: "le nombre de places autorisées et offertes en accueil de jour des enfants, par mode d'accueil (préscolaire, parascolaire, familial) et par type d'institution, tel que publié par StatVD.",
  },
  {
    key: 'vd.aje.coverage_pct',
    en: 'AJE coverage rate', de: 'AJE-Deckungsgrad', fr: "Taux de couverture de l'accueil de jour",
    unit: 'pct', geo_level: 'organisation', period_kind: 'year', dims: ['mode'],
    source_key: 'statvd-accueil-jour-enfants',
    definition: "le taux de couverture en places d'accueil préscolaire, par réseau, tel que publié dans T_Préscolaire_tx_couverture.",
  },
  {
    key: 'vd.aje.family_carers',
    en: 'Registered family day-carers', de: 'registrierte Tagesfamilien', fr: "Accueillantes en milieu familial",
    unit: 'persons', geo_level: 'organisation', period_kind: 'year', dims: [],
    source_key: 'statvd-accueil-jour-enfants',
    definition: "le nombre d'accueillantes en milieu familial au 31 décembre, tel que publié par StatVD (T_Accueil_familial).",
  },
  {
    key: 'vd.aje.institutions',
    en: 'AJE institutions', de: 'AJE-Institutionen', fr: "Institutions d'accueil de jour",
    unit: 'institutions', geo_level: 'canton', period_kind: 'year', dims: ['measure'],
    source_key: 'statvd-accueil-jour-enfants',
    definition: "le nombre d'institutions d'accueil collectif autorisées et subventionnées par le SCAJE, tel que publié dans les Chiffres-clés de l'accueil de jour des enfants.",
  },
  {
    key: 'vd.aje.attendance_pct',
    en: 'AJE attendance rate', de: 'AJE-Belegungsrate', fr: "Taux de fréquentation de l'accueil de jour",
    unit: 'pct', geo_level: 'canton', period_kind: 'year', dims: [],
    source_key: 'statvd-accueil-jour-enfants',
    definition: "le taux de fréquentation des structures d'accueil collectif, tel que publié par StatVD (Résultats principaux AJE).",
  },
  {
    key: 'vd.lausanne.subsidies_chf',
    en: 'Lausanne subsidies by department', de: 'Lausanner Subventionen nach Direktion', fr: 'Subventions de la Ville de Lausanne par direction',
    unit: 'chf', geo_level: 'commune', period_kind: 'year', dims: ['department', 'budget_kind'],
    source_key: 'lausanne-budget-subsidies',
    definition: "le sous-total « SUBVENTIONS TOTALES DE LA DIRECTION » de chaque direction municipale, tel que publié dans la brochure du budget ou des comptes de la Ville de Lausanne.",
  },
];

export function seriesByKey(key: string): SeriesSpec | null {
  return SERIES.find((s) => s.key === key) ?? null;
}

export function seriesLabel(lang: 'en' | 'de' | 'fr', key: string): string {
  const spec = seriesByKey(key);
  if (!spec) return key;
  return spec[lang] ?? spec.en;
}

// ── dimension labels ─────────────────────────────────────────────────────────────────────────
//
// Three languages per token, like raster labels — NOT i18n catalogue keys. Kept flat, one record
// per dimension, so a wave-2/3 module extending DIMENSIONS finds exactly one place to add labels
// too (`dimensionLabelTables` below); an unlabelled token still answers with itself in every
// language rather than throwing, so a module that forgets a label fails its dry run's
// "every dim token in DIMENSIONS" check, not this function.

type DimLabelTable = Record<string, readonly [string, string, string]>;

const REGIME_LABELS: DimLabelTable = {
  ri: ["Social aid (RI)", 'Sozialhilfe (RI)', "Revenu d'insertion (RI)"],
  ai: ['Disability insurance (AI)', 'Invalidenversicherung (IV)', 'Assurance-invalidité (AI)'],
  laci: ['Unemployment insurance (LACI)', 'Arbeitslosenversicherung (AVIG)', 'Assurance-chômage (LACI)'],
  asylum: ['Asylum (EVAM)', 'Asyl (EVAM)', 'Asile (EVAM)'],
  refugee_integration: ['Refugee integration (CSIR · BCI)', 'Flüchtlingsintegration (CSIR · BCI)', 'Intégration des réfugiés (CSIR · BCI)'],
  adult_basic_education: ['Adult basic education (CIFÉA)', 'Grundbildung Erwachsene (CIFÉA)', 'Formation de base des adultes (CIFÉA)'],
  post_compulsory: ['Post-compulsory education (DGEP)', 'Nachobligatorische Bildung (DGEP)', 'Enseignement postobligatoire (DGEP)'],
  child_youth_protection: ['Child and youth protection (DGEJ)', 'Kinder- und Jugendschutz (DGEJ)', "Protection de l'enfance et de la jeunesse (DGEJ)"],
  cantonal_disability: ['Cantonal disability mandate (LAIH)', 'Kantonaler Behindertenauftrag', 'Mandat cantonal handicap (LAIH)'],
  health: ['Health (hospitals · CMS)', 'Gesundheit (Spitäler · CMS)', 'Santé (hôpitaux · CMS)'],
  justice: ['Justice (courts · probation)', 'Justiz (Gerichte · Bewährungshilfe)', 'Justice (tribunaux · probation)'],
  schools: ['Schools', 'Schulen', 'Écoles'],
  private: ['Private clients and employers', 'Private und Arbeitgeber', 'Privés et employeurs'],
  other: ['Other', 'Andere', 'Autre'],
  pc_familles: ['Family supplementary benefits (PC familles)', 'Familienergänzungsleistungen (PC familles)', 'Prestations complémentaires pour familles (PC familles)'],
  pc_avs_ai: ['AVS/AI supplementary benefits', 'EL zu AHV/IV', 'Prestations complémentaires AVS/AI'],
  lamal_subsidy: ['Health-insurance subsidy (LAMal)', 'Prämienverbilligung (KVG)', 'Subside LAMal'],
  rente_pont: ['Bridging pension', 'Überbrückungsrente', 'Rente-pont'],
  laprams: ['Cantonal maternity/adoption allowance (LAPRAMS)', 'Kantonale Mutterschafts-/Adoptionszulage (LAPRAMS)', "Allocation cantonale de maternité/adoption (LAPRAMS)"],
  housing_aid: ['Individual housing aid', 'Individuelle Wohnbeihilfe', 'Aide individuelle au logement'],
  maternity: ['Maternity insurance', 'Mutterschaftsversicherung', 'Assurance maternité'],
  alimony_advance: ['Alimony advance', 'Alimentenbevorschussung', "Avance sur pension alimentaire"],
};

const AGE_BAND_LABELS: DimLabelTable = {
  '0_6': ['0–6 years', '0–6 Jahre', '0–6 ans'],
  '7_15': ['7–15 years', '7–15 Jahre', '7–15 ans'],
  '16_plus': ['16 and over', '16 und älter', '16 ans et plus'],
  '18_64': ['18–64 years', '18–64 Jahre', '18–64 ans'],
  lt6: ['Under 6', 'Unter 6', 'Moins de 6 ans'],
  '6_15': ['6–15 years', '6–15 Jahre', '6–15 ans'],
  '65_plus': ['65 and over', '65 und älter', '65 ans et plus'],
  all: ['All ages', 'Alle Altersgruppen', 'Tous âges'],
};

const GENDER_LABELS: DimLabelTable = {
  f: ['Women', 'Frauen', 'Femmes'],
  m: ['Men', 'Männer', 'Hommes'],
  nb: ['Non-binary', 'Nichtbinär', 'Non binaire'],
  all: ['All genders', 'Alle Geschlechter', 'Tous genres'],
};

const OFFENCE_TYPE_LABELS: DimLabelTable = {
  homicides: ['Homicides (incl. attempts)', 'Tötungsdelikte (inkl. Versuche)', 'Homicides (y compris tentatives)'],
  lesions_corporelles: ['Bodily harm', 'Körperverletzung', 'Lésions corporelles'],
  actes_ordre_sexuel_enfants: ['Sexual acts with children', 'Sexuelle Handlungen mit Kindern', "Actes d'ordre sexuel avec des enfants"],
  autres_atteintes_integrite_sexuelle: ['Other sexual offences', 'Andere Sexualdelikte', "Autres atteintes à l'intégrité sexuelle"],
  accidents_circulation_routiere: ['Road traffic accidents', 'Strassenverkehrsunfälle', 'Accidents de la circulation routière'],
  autres_infractions: ['Other offences', 'Andere Delikte', 'Autres infractions'],
  sans_indication: ['Not indicated', 'Ohne Angabe', 'Sans indication'],
};

const CLAIMANT_STATUS_LABELS: DimLabelTable = {
  victim: ['Victim', 'Opfer', 'Victime'],
  relative: ['Relative', 'Angehörige/r', 'Proche'],
};

const SERVICE_TYPE_LABELS: DimLabelTable = {
  hebergement: ['Residential care', 'Unterbringung', 'Hébergement'],
  centre_de_jour: ['Day centre', 'Tageszentrum', 'Centre de jour'],
  atelier: ['Workshop', 'Werkstatt', 'Atelier'],
  ensemble: ['All prestations', 'Alle Leistungen', 'Ensemble'],
  centre_de_jour_atelier: ['Day centre + workshop', 'Tageszentrum + Werkstatt', 'Centre de jour + atelier'],
  hebergement_atelier: ['Residential care + workshop', 'Unterbringung + Werkstatt', 'Hébergement + atelier'],
  hebergement_centre_de_jour: ['Residential care + day centre', 'Unterbringung + Tageszentrum', 'Hébergement + centre de jour'],
  hebergement_centre_de_jour_atelier: ['Residential care + day centre + workshop', 'Unterbringung + Tageszentrum + Werkstatt', 'Hébergement + centre de jour + atelier'],
};

const PROBLEM_LABELS: DimLabelTable = {
  deficience_intellectuelle: ['Intellectual disability', 'Geistige Behinderung', 'Déficience intellectuelle'],
  troubles_psychiques: ['Psychiatric disorders', 'Psychische Störungen', 'Troubles psychiques'],
  addiction: ['Addiction', 'Sucht', 'Addiction'],
  deficience_physique: ['Physical disability', 'Körperbehinderung', 'Déficience physique'],
  deficience_sensorielle: ['Sensory disability', 'Sinnesbehinderung', 'Déficience sensorielle'],
  polyhandicap: ['Multiple disabilities', 'Mehrfachbehinderung', 'Polyhandicap'],
  autre: ['Other', 'Andere', 'Autre'],
  sans_indication: ['Not indicated', 'Ohne Angabe', 'Sans indication'],
  tous: ['All', 'Alle', 'Tous'],
};

const MODE_LABELS: DimLabelTable = {
  prescolaire: ['Pre-school', 'Vorschule', 'Préscolaire'],
  parascolaire: ['Out-of-school', 'Ausserschulisch', 'Parascolaire'],
  familial: ['Family day-care', 'Tagesfamilie', 'Accueil familial'],
  collectif: ['Collective', 'Kollektiv', 'Collectif'],
  total: ['Total', 'Total', 'Total'],
};

const MEASURE_LABELS: DimLabelTable = {
  authorised: ['Authorised', 'Bewilligt', 'Autorisées'],
  offered: ['Offered', 'Angeboten', 'Offertes'],
  offered_fte: ['Offered, FTE', 'Angeboten, VZÄ', 'Offertes, EPT'],
  offered_morning: ['Offered, morning', 'Angeboten, morgens', 'Offertes, matin'],
  offered_noon: ['Offered, midday', 'Angeboten, mittags', 'Offertes, midi'],
  offered_afternoon: ['Offered, afternoon', 'Angeboten, nachmittags', 'Offertes, après-midi'],
  authorised_incl_own: ["Authorised, incl. carer's own children", 'Bewilligt, inkl. eigene Kinder', "Autorisées, y c. les enfants de l'accueillante"],
  authorised_extra_pupils: ['Authorised, extra schoolchildren', 'Bewilligt, zusätzliche Schulkinder', 'Autorisées, écoliers supplémentaires'],
  offered_fte_prescolaire: ['Offered FTE, pre-school', 'Angeboten VZÄ, Vorschule', 'Offertes EPT, préscolaire'],
  offered_fte_parascolaire: ['Offered FTE, out-of-school', 'Angeboten VZÄ, ausserschulisch', 'Offertes EPT, parascolaire'],
  subsidised: ['Subsidised', 'Subventioniert', 'Subventionnées'],
};

const STAFF_FUNCTION_LABELS: DimLabelTable = {
  direction_pedagogique: ['Pedagogical management', 'Pädagogische Leitung', 'Direction pédagogique'],
  educatif_tertiaire: ['Educational staff, tertiary', 'Betreuungspersonal, Tertiärstufe', 'Personnel éducatif, tertiaire'],
  educatif_secondaire_ii: ['Educational staff, upper secondary', 'Betreuungspersonal, Sekundarstufe II', 'Personnel éducatif, secondaire II'],
  educatif_ape: ['Educational staff, ASE', 'Betreuungspersonal, FaBe', 'Personnel éducatif, ASE'],
  stagiaire_tertiaire: ['Intern, tertiary', 'Praktikant/in, Tertiärstufe', 'Stagiaire tertiaire'],
  apprenti_ase: ['Apprentice, ASE', 'Lernende/r, FaBe', 'Apprenti·e ASE'],
  direction_administrative: ['Administrative management', 'Administrative Leitung', 'Direction administrative'],
  administration: ['Administration', 'Verwaltung', 'Administration'],
  logistique: ['Logistics', 'Logistik', 'Logistique'],
  total: ['Total', 'Total', 'Total'],
};

const INSTITUTION_TYPE_LABELS: DimLabelTable = {
  collectif_subventionne: ['Collective, subsidised', 'Kollektiv, subventioniert', 'Collectif subventionné'],
  collectif_subventionne_elargi: ['Collective, subsidised (extended hours)', 'Kollektiv, subventioniert (erweitert)', 'Collectif subventionné, temps élargi'],
  collectif_subventionne_restreint: ['Collective, subsidised (reduced hours)', 'Kollektiv, subventioniert (reduziert)', 'Collectif subventionné, temps restreint'],
  collectif_non_subventionne: ['Collective, not subsidised', 'Kollektiv, nicht subventioniert', 'Collectif non subventionné'],
  ecole_privee: ['Private school', 'Privatschule', 'École privée'],
  total: ['Total', 'Total', 'Total'],
};

const CONTRIBUTOR_LABELS: DimLabelTable = {
  etat: ['Canton of Vaud', 'Kanton Waadt', "État de Vaud"],
  employeurs: ['Employers', 'Arbeitgeber', 'Employeurs'],
  communes: ['Communes', 'Gemeinden', 'Communes'],
  loterie_romande: ['Loterie Romande', 'Loterie Romande', 'Loterie Romande'],
};

const PLACEMENT_TYPE_LABELS: DimLabelTable = {
  institution_pse: ['Institution (PSE contract)', 'Institution (PSE-Vertrag)', 'Institution PSE'],
  institution_non_pse: ['Institution (no PSE contract)', 'Institution (ohne PSE-Vertrag)', 'Institution non PSE'],
  institution_hors_canton: ['Out-of-canton institution', 'Ausserkantonale Institution', 'Institution hors canton'],
  famille_accueil_vaudoise: ['Foster family, Vaud', 'Pflegefamilie, Waadt', "Famille d'accueil vaudoise"],
  famille_accueil_elargie: ['Foster family, extended', 'Pflegefamilie, erweitert', "Famille d'accueil élargie"],
  famille_accueil_hors_canton: ['Foster family, out-of-canton', 'Pflegefamilie, ausserkantonal', "Famille d'accueil hors canton"],
  studio: ['Studio', 'Studio', 'Studio'],
  pension: ['Boarding house', 'Pension', 'Pension'],
  lieu_accueil_mere_enfant: ['Mother-child housing', 'Mutter-Kind-Wohnheim', "Lieu d'accueil mère-enfant"],
};

const REQUEST_TYPE_LABELS: DimLabelTable = {
  signalement: ['Report', 'Meldung', 'Signalement'],
  demande_aide: ['Request for help', 'Hilfeersuchen', "Demande d'aide"],
  mandat_judiciaire: ['Judicial mandate', 'Gerichtlicher Auftrag', 'Mandat judiciaire'],
  rapport_police: ['Police report', 'Polizeibericht', 'Rapport de police'],
  information_sans_signalement: ['Information, not a report', 'Information ohne Meldung', 'Information ne valant pas signalement'],
};

const MOTIF_LABELS: DimLabelTable = {
  abus_ordre_sexuel: ['Sexual abuse', 'Sexueller Missbrauch', "Abus d'ordre sexuel"],
  circonstances_entravant_capacite_parentale: ['Circumstances impairing parenting capacity', 'Umstände, die die Erziehungsfähigkeit beeinträchtigen', 'Circonstances entravant la capacité parentale'],
  difficultes_educatives: ['Educational difficulties', 'Erziehungsschwierigkeiten', 'Difficultés éducatives'],
  mauvais_traitements_physiques: ['Physical mistreatment', 'Körperliche Misshandlung', 'Mauvais traitements physiques'],
  mauvais_traitements_psychiques: ['Psychological mistreatment', 'Psychische Misshandlung', 'Mauvais traitements psychiques'],
  mise_en_danger_par_lui_meme: ['Self-endangerment', 'Selbstgefährdung', 'Mise en danger par lui-même'],
  negligences_carences: ['Neglect', 'Vernachlässigung', 'Négligences, carences'],
  problemes_droit_de_visite: ['Visitation-rights issues', 'Probleme beim Besuchsrecht', 'Problèmes de droit de visite'],
  violence_domestique: ['Domestic violence', 'Häusliche Gewalt', 'Violence domestique'],
};

const DOMAIN_LABELS: DimLabelTable = {
  langue_formations: ['Language training', 'Sprachausbildung', 'Langue et formations'],
  protection_discrimination: ['Protection against discrimination', 'Diskriminierungsschutz', 'Protection contre la discrimination'],
  conseil: ['Counselling', 'Beratung', 'Conseil'],
  employabilite: ['Employability', 'Beschäftigungsfähigkeit', 'Employabilité'],
  forfaits: ['Lump-sum schemes', 'Pauschalen', 'Forfaits'],
  vivre_ensemble: ['Living together', 'Zusammenleben', 'Vivre ensemble'],
  primo_information: ['First information', 'Erstinformation', 'Primo-information'],
  information_et_conseil: ['Information and counselling', 'Information und Beratung', 'Information et conseil'],
  petite_enfance: ['Early childhood', 'Frühe Kindheit', 'Petite enfance'],
};

const HOUSEHOLD_TYPE_LABELS: DimLabelTable = {
  single: ['Single person', 'Einzelperson', 'Personne seule'],
  single_parent: ['Single parent', 'Alleinerziehend', 'Famille monoparentale'],
  couple: ['Couple, no children', 'Paar ohne Kinder', 'Couple sans enfant'],
  couple_with_children: ['Couple with children', 'Paar mit Kindern', 'Couple avec enfants'],
  other: ['Other', 'Andere', 'Autre'],
};

const DEPARTMENT_LABELS: DimLabelTable = {
  culture_et_developpement_urbain: ['Culture and urban development', 'Kultur und Stadtentwicklung', 'Culture et développement urbain'],
  securite_et_economie: ['Security and economy', 'Sicherheit und Wirtschaft', 'Sécurité et économie'],
  sports_et_cohesion_sociale: ['Sports and social cohesion', 'Sport und soziale Kohäsion', 'Sports et cohésion sociale'],
  logement_environnement_et_architecture: ['Housing, environment and architecture', 'Wohnen, Umwelt und Architektur', 'Logement, environnement et architecture'],
  enfance_jeunesse_et_quartiers: ['Childhood, youth and neighbourhoods', 'Kindheit, Jugend und Quartiere', 'Enfance, jeunesse et quartiers'],
  finances_et_mobilite: ['Finance and mobility', 'Finanzen und Mobilität', 'Finances et mobilité'],
};

const BUDGET_KIND_LABELS: DimLabelTable = {
  budget: ['Budget', 'Budget', 'Budget'],
  comptes: ['Accounts', 'Rechnung', 'Comptes'],
};

const DIM_LABEL_TABLES: Record<string, DimLabelTable> = {
  regime: REGIME_LABELS,
  age_band: AGE_BAND_LABELS,
  gender: GENDER_LABELS,
  offence_type: OFFENCE_TYPE_LABELS,
  claimant_status: CLAIMANT_STATUS_LABELS,
  service_type: SERVICE_TYPE_LABELS,
  problem: PROBLEM_LABELS,
  mode: MODE_LABELS,
  measure: MEASURE_LABELS,
  staff_function: STAFF_FUNCTION_LABELS,
  institution_type: INSTITUTION_TYPE_LABELS,
  contributor: CONTRIBUTOR_LABELS,
  placement_type: PLACEMENT_TYPE_LABELS,
  request_type: REQUEST_TYPE_LABELS,
  motif: MOTIF_LABELS,
  domain: DOMAIN_LABELS,
  household_type: HOUSEHOLD_TYPE_LABELS,
  department: DEPARTMENT_LABELS,
  budget_kind: BUDGET_KIND_LABELS,
};

const LANG_INDEX: Record<'en' | 'de' | 'fr', 0 | 1 | 2> = { en: 0, de: 1, fr: 2 };

export function dimLabel(lang: 'en' | 'de' | 'fr', dim: string, token: string): string {
  const row = DIM_LABEL_TABLES[dim]?.[token];
  if (!row) return token;
  return row[LANG_INDEX[lang]] ?? token;
}

// ── the row shape (§2.1, §7's deviation) ────────────────────────────────────────────────────

export type SeriesQualifier = 'at_least' | 'at_most' | 'approx';

/** Mirrors `series_points` (schema.sql §2.1). */
export interface SeriesPoint {
  id: number;
  series_key: string;
  source_key: string;
  document_id: number;
  geo_level: SeriesGeoLevel;
  geo_code: string;
  geo_name: string;
  period_kind: SeriesPeriodKind;
  period: string;
  dims: Record<string, string>;
  value: number;
  unit: SeriesUnit;
  qualifier: SeriesQualifier | null;
  quote: string;
  as_of: string | null;
  status: 'current' | 'superseded';
  superseded_by: number | null;
  created_at: string;
}
